import { IPromise, IPromiseResult, PromiseResultType } from "./Async";
import { Assert } from "../Utils/Assert";

enum CallbackType {
    OnSuccess,
    OnError,
    OnFinish,
}

interface IPair<TKey, TValue> {
    key: TKey;
    value: TValue;
}

export class Promise<TResult, TErrors> implements IPromise<TResult, TErrors> {
    private _callbacks: IPair<CallbackType, (result?: any) => void>[];

    private _isFinished = false;

    private _result: IPromiseResult<TResult, TErrors>;
    constructor(func: (onSuccess: (param: TResult) => void, onError: (param: TErrors) => void) => void) {
        this._callbacks = [];

        func(
            (data: TResult) => this.runSuccess(data),
            (data: TErrors) => this.runError(data)
        );
    }

    public isFinished(): boolean {
        return this._isFinished;
    }

    public getResult(): IPromiseResult<TResult, TErrors> {
        return this._result;
    }

    public onSuccess(callback: (param: TResult) => void): Promise<TResult, TErrors> {
        Assert(!this._isFinished, "Attempting to assign onSuccess callback to finished Promise");
        this._callbacks.push({ key: CallbackType.OnSuccess, value: callback });
        return this;
    }

    public onError(callback: (param: TErrors) => void): Promise<TResult, TErrors> {
        Assert(!this._isFinished, "Attempting to assign onError callback to finished Promise");
        this._callbacks.push({ key: CallbackType.OnError, value: callback });

        return this;
    }

    public onFinish(callback: () => void): Promise<TResult, TErrors> {
        Assert(!this._isFinished, "Attempting to assign onFinished callback to finished Promise");
        this._callbacks.push({ key: CallbackType.OnFinish, value: callback });

        return this;
    }

    private runSuccess(data: TResult): void {
        this._isFinished = true;
        this._result = { type: PromiseResultType.Success, result: data };

        this._callbacks.forEach((pair: IPair<CallbackType, (result: TResult) => void>) => {
            if (pair.key === CallbackType.OnSuccess || pair.key === CallbackType.OnFinish) {
                pair.value(data);
            }
        });
    }

    private runError(data: TErrors): void {
        this._isFinished = true;
        this._result = { type: PromiseResultType.Error, errors: data };

        this._callbacks.forEach((pair: IPair<CallbackType, (result: TErrors) => void>) => {
            if (pair.key === CallbackType.OnError || pair.key === CallbackType.OnFinish) {
                pair.value(data);
            }
        });
    }
}
